import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { X, ArrowLeft, ArrowRight, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export type TourStep = { target: string; title: string; body: string; tab?: string };

const pad = 10;

function useRect(target: string | null) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    if (!target) { setRect(null); return; }
    let el: HTMLElement | null = null;
    let tries = 0;
    const update = () => el && setRect(el.getBoundingClientRect());
    let t: any;
    const find = () => {
      el = document.querySelector(`[data-testid="${target}"]`) as HTMLElement | null;
      if (!el) { if (tries++ < 10) { t = setTimeout(find, 100); } else { setRect(null); } return; }
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      t = setTimeout(update, 350);
    };
    find();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { clearTimeout(t); window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [target]);
  return rect;
}

function Bubble({ step, i, n, rect, onBack, onNext, onClose, dontShow, setDontShow }: any) {
  const ref = useRef<HTMLDivElement>(null);
  const [h, setH] = useState(300);
  useLayoutEffect(() => { if (ref.current) setH(ref.current.offsetHeight); }, [i, rect]);
  const vh = window.innerHeight;
  const below = !rect || rect.bottom + pad + 10 + h < vh;
  const above = rect && rect.top - pad - 10 - h > 0;
  const w = Math.min(380, window.innerWidth - 24);
  const left = rect ? Math.max(12, Math.min(rect.left, window.innerWidth - w - 12)) : (window.innerWidth - w) / 2;
  const raw = !rect ? vh / 2 - h / 2 : below ? rect.bottom + pad + 10 : above ? rect.top - pad - 10 - h : vh - h - 12;
  const top = Math.max(12, Math.min(raw, vh - h - 12));
  const last = i === n - 1;
  return (
    <div ref={ref} role="dialog" aria-label={step.title} className="fixed z-[1002] max-h-[calc(100vh-24px)] overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-black/5 transition-[top,left] duration-300" style={{ top, left, width: w }} data-testid="tour-bubble">
      <div className="mb-1 flex items-start justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-wider text-[#B45309]" data-testid="tour-step-count">Step {i + 1} of {n}</p>
        <button onClick={onClose} aria-label="Close tour" className="text-[#64748B] hover:text-[#0F172A]" data-testid="tour-close-button"><X className="h-4 w-4" /></button>
      </div>
      <h3 className="text-lg font-bold text-[#0F172A]" data-testid="tour-step-title">{step.title}</h3>
      <p className="mt-1.5 text-[15px] leading-relaxed text-[#334155]">{step.body}</p>
      <div className="mt-4 h-1 overflow-hidden rounded-full bg-[#F1EFEA]"><div className="h-full bg-[#1B365D] transition-[width] duration-300" style={{ width: `${((i + 1) / n) * 100}%` }} /></div>
      <label className="mt-4 flex items-center gap-2 text-sm text-[#475569]">
        <input type="checkbox" checked={dontShow} onChange={(e) => setDontShow(e.target.checked)} className="h-4 w-4 accent-[#1B365D]" data-testid="tour-dont-show-checkbox" />
        Don't show this walkthrough again
      </label>
      <div className="mt-4 flex items-center justify-between">
        <button onClick={onBack} disabled={i === 0} className="btn-secondary btn-sm disabled:opacity-40" data-testid="tour-back-button"><ArrowLeft className="h-4 w-4" /> Back</button>
        <button onClick={onNext} className="btn-primary btn-sm" data-testid="tour-next-button">{last ? "Finish" : <>Next <ArrowRight className="h-4 w-4" /></>}</button>
      </div>
    </div>
  );
}

export function Walkthrough({ id, steps, autoStart, onTab }: { id: string; steps: TourStep[]; autoStart: boolean; onTab?: (tab: string) => void }) {
  const { user, setUser } = useAuth() as any;
  const [i, setI] = useState<number | null>(null);
  const [dontShow, setDontShow] = useState(true);
  const seen = !!user?.tours_done?.includes(id);
  useEffect(() => {
    if (autoStart && user && !seen) { const t = setTimeout(() => setI(0), 900); return () => clearTimeout(t); }
  }, [autoStart, user, seen]);
  const rect = useRect(i === null ? null : steps[i].target);
  useEffect(() => { if (i !== null && steps[i].tab) { onTab?.(steps[i].tab!); } }, [i]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = async () => {
    setI(null);
    if (dontShow && !seen) {
      try { setUser((await api.post("/auth/me/tour", { tour: id })).data); } catch { /* ignore */ }
    }
  };
  const start = () => { setDontShow(true); setI(0); };
  return (
    <>
      <button onClick={start} className="btn-secondary btn-sm" data-testid="tour-start-button"><Compass className="h-4 w-4" /> Take the tour</button>
      {i !== null && (
        <>
          <div className="fixed inset-0 z-[1000]" onClick={close} data-testid="tour-overlay" />
          <div className="pointer-events-none fixed z-[1001] rounded-2xl ring-4 ring-[#F59E0B] transition-all duration-300"
            style={rect ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, boxShadow: "0 0 0 9999px rgba(15,23,42,0.55)" } : { inset: "50% 50%", boxShadow: "0 0 0 9999px rgba(15,23,42,0.55)" }} />
          <Bubble step={steps[i]} i={i} n={steps.length} rect={rect} dontShow={dontShow} setDontShow={setDontShow}
            onBack={() => setI(Math.max(0, i - 1))} onNext={() => (i === steps.length - 1 ? close() : setI(i + 1))} onClose={close} />
        </>
      )}
    </>
  );
}
